import { CartLinesUpdateEvent } from '@shopify/events';

// La protezione spedizione (prodotto nascosto) non conta per la soglia della spedizione gratuita.
const PROTECTION_HANDLE = 'protezione-spedizione';
const shippingTotal = (cart) =>
  Number(cart.total_price) -
  (cart.items || [])
    .filter((item) => item.handle === PROTECTION_HANDLE || item.product?.handle === PROTECTION_HANDLE)
    .reduce((sum, item) => sum + Number(item.final_line_price ?? item.line_price ?? 0), 0);


class GnTopbar extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback();
    this._generation = (this._generation || 0) + 1;
    this._connectedGeneration = this._generation;
    this.progress = this.querySelector('[data-topbar-progress]');
    this.countdownEl = this.querySelector('[data-topbar-countdown]');
    this.dismissBtn = this.querySelector('[data-topbar-dismiss]');
    this._onDismiss = () => this.dismiss();
    this._onCartUpdate = (event) => this.handleCartUpdate(event, this._connectedGeneration);

    this.dismissBtn?.addEventListener('click', this._onDismiss);
    if (this.countdownEl) this.startCountdown();
    if (this.progress) {
      document.addEventListener(CartLinesUpdateEvent.eventName || 'shopify:cart:lines-update', this._onCartUpdate);
    }
  }

  disconnectedCallback() {
    this._generation = (this._generation || 0) + 1;
    if (this._timer) window.clearInterval(this._timer);
    this._timer = undefined;
    this.dismissBtn?.removeEventListener('click', this._onDismiss);
    if (this._onCartUpdate) {
      document.removeEventListener(CartLinesUpdateEvent.eventName || 'shopify:cart:lines-update', this._onCartUpdate);
    }
    this._onDismiss = undefined;
    this._onCartUpdate = undefined;
  }

  dismiss() {
    const key = this.dataset.dismissKey;
    const days = parseInt(this.dataset.dismissDays || '7', 10);
    try {
      localStorage.setItem(key, String(Date.now() + days * 86400000));
    } catch {
      // Private browsing/localStorage-disabled stores still dismiss this view.
    }
    const bar = this.closest('.announcement-bar');
    const bg = bar?.previousElementSibling;
    if (bar) bar.style.display = 'none';
    if (bg?.classList.contains('section-background')) bg.style.display = 'none';
  }

  async handleCartUpdate(event, generation) {
    if (!event?.promise) return;
    try {
      const result = await event.promise;
      if (!this.isConnected || generation !== this._generation) return;
      const cart = result?.cart;
      if (cart && Number.isFinite(Number(cart.total_price)) && Array.isArray(cart.items)) {
        this.renderProgress(shippingTotal(cart), cart.item_count, cart.currency?.iso_code || cart.currency);
        return;
      }
      await this.refreshCart(generation);
    } catch {
      // Cart failures are rendered by the owning cart component; never issue a second fetch.
    }
  }

  async refreshCart(generation = this._generation) {
    if (!this.isConnected || generation !== this._generation) return;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      if (!this.isConnected || generation !== this._generation) return;
      this.renderProgress(shippingTotal(cart), cart.item_count, cart.currency?.iso_code || cart.currency);
    } catch {
      // A failed read must not turn a successful cart mutation into an error.
    }
  }

  renderProgress(total, itemCount, currency) {
    const progress = this.progress;
    const threshold = parseInt(progress?.dataset.threshold || '0', 10);
    if (!progress || !threshold) return;
    const numericTotal = Number(total) || 0;
    const numericItems = Number(itemCount) || 0;
    const onlyWithItems = progress.dataset.onlyWithItems === 'true';
    this.classList.toggle('is-cart-active', numericItems > 0);
    progress.hidden = onlyWithItems && numericItems === 0;
    const remaining = Math.max(threshold - numericTotal, 0);
    progress.style.setProperty('--topbar-progress', `${Math.min((numericTotal / threshold) * 100, 100)}%`);
    const label = progress.querySelector('[data-topbar-progress-text]');
    if (!label) return;
    if (remaining > 0) {
      const money = new Intl.NumberFormat(document.documentElement.lang || 'it-IT', {
        style: 'currency',
        currency: currency || progress.dataset.currency || 'EUR',
        minimumFractionDigits: remaining % 100 === 0 ? 0 : 2,
      }).format(remaining / 100);
      label.textContent = progress.dataset.msgRemaining.replace('{remaining}', money);
    } else {
      label.textContent = progress.dataset.msgUnlocked;
    }
  }

  startCountdown() {
    if (this._timer) window.clearInterval(this._timer);
    const tick = () => {
      if (this.isConnected) this.renderCountdown();
    };
    tick();
    this._timer = window.setInterval(tick, 30000);
  }

  shopNow(timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value;
    const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return { day: days[get('weekday')], hour: parseInt(get('hour'), 10), minute: parseInt(get('minute'), 10) };
  }

  renderCountdown() {
    const element = this.countdownEl;
    if (!element) return;
    const [cutoffHour, cutoffMinute] = (element.dataset.cutoff || '14:00').split(':').map(Number);
    const workdays = (element.dataset.workdays || '1,2,3,4,5').split(',').map(Number);
    const now = this.shopNow(element.dataset.tz || 'Europe/Rome');
    const minutesNow = now.hour * 60 + now.minute;
    const minutesCutoff = cutoffHour * 60 + cutoffMinute;
    if (workdays.includes(now.day) && minutesNow < minutesCutoff) {
      const left = minutesCutoff - minutesNow;
      const text = left >= 60 ? `${Math.floor(left / 60)}h ${left % 60}m` : `${left}m`;
      element.textContent = element.dataset.msg.replace('{time}', text);
    } else {
      const names = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
      let next = (now.day + 1) % 7;
      let guard = 0;
      while (!workdays.includes(next) && guard++ < 7) next = (next + 1) % 7;
      element.textContent = element.dataset.msgClosed.replace('{day}', names[next]);
    }
    element.hidden = false;
  }
}

if (!customElements.get('gn-topbar')) customElements.define('gn-topbar', GnTopbar);
